import { Card, IndexTable, Page, Tabs, Text } from '@shopify/polaris';
import { FilterSingleDateCategories } from '../../UI/Reports/FilterDatesCategories';
import { useEffect, useState } from 'react';
import { getReportsDepositsValuation } from '../../../services/reports/sellsService';
import { VerticalSpace } from '../../UI/Reports/VerticalSpace';
import PieChart from '../../UI/Graphs/PieChart';
import HorizontalBarsAnimatedChart from '../../UI/Graphs/HorizontalBarsAnimatedChart';
import { getSearchParams } from '../../../helpers/handleSearchParams';
import { useSearchParams } from '../../../customHooks/useSearchParams';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import { displayInTz } from '../../../helpers/dates';

const getReports = (filters) =>
  getReportsDepositsValuation(
    Object.keys(filters)
      .map((k) => (filters[k] ? { slug: k, value: filters[k] } : null))
      .filter((f) => f)
      .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {}),
  );

const tabsMenu = [
  { id: 'deposits', content: 'Por Depósitos', component: ({ values }) => <TabDeposits values={values} /> },
  { id: 'gender', content: 'Por Género', component: ({ values }) => <TabGender values={values} /> },
  { id: 'category', content: 'Por Categoría', component: ({ values }) => <TabCategory values={values} /> },
];

const ViewReportDeposits = () => {
  const [values, setValues] = useState(null);
  const { searchParams } = useSearchParams();
  const { timezone } = useTimezone();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [currentTab, setCurrentTab] = useState(tabsMenu[0]);

  const handleSelect = (selectedTabIndex) => {
    setCurrentTab(tabsMenu[selectedTabIndex]);
    setCurrentTabIndex(selectedTabIndex);
  };

  useEffect(() => {
    getReports(searchParams).then((r) => setValues(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const rawDate = getSearchParams('date');
  const subtitleDate = rawDate
    ? displayInTz(rawDate, timezone)
    : new Date().toLocaleDateString();

  return (
    <Page title="Valoración Existencias" subtitle={`A fecha de ${subtitleDate}`}>
      <>
        <FilterSingleDateCategories />
        <VerticalSpace />
        {values && (
          <Card>
            <Tabs tabs={tabsMenu} selected={currentTabIndex} onSelect={handleSelect}>
              <VerticalSpace size="small" />
              <hr />
              <VerticalSpace size="medium" />
              <currentTab.component values={values} />
            </Tabs>
          </Card>
        )}
      </>
    </Page>
  );
};

/**
 * PESTAÑAS
 */

const TabDeposits = ({ values }) => {
  const totals = ['CENTRAL', 'EPH', 'TIENDA OUTLET'].map((t) => ({
    name: t,
    ...values.byDeposit
      .filter((v) => v.name.match(t))
      .reduce(
        (acc, cur) => {
          acc.qty += cur.qty;
          acc.families += cur.families;
          acc.valuePvf += cur.valuePvf;
          return acc;
        },
        {
          qty: 0,
          families: 0,
          valuePvf: 0,
        },
      ),
  }));

  const headings = [
    { title: 'Depósito', key: 'name', defaultSortDirection: 'descending' },
    { title: 'Uds.', alignment: 'end', key: 'qty', defaultSortDirection: 'descending' },
    { title: 'Serie-Color', alignment: 'end', key: 'families', defaultSortDirection: 'descending' },
    { title: 'PVF', alignment: 'end', key: 'valuePvf', defaultSortDirection: 'descending' },
  ];

  return (
    <>
      <div className="flex flex-row justify-around">
        <div className="w-1/3">
          <PieChart
            data={totals.map((e) => ({
              value: e.qty,
              name: e.name,
            }))}
          />
        </div>
        <div className="w-1/2 flex items-center justify-center">
          <div className="w-full">
            <PrettyTable values={totals} headings={headings} sum={true} />
          </div>
        </div>
      </div>
      <div className="p-4 py-8">
        <PrettyTable values={values.byDeposit} headings={headings} sum={true} isSortable={true} />
      </div>
    </>
  );
};

const TabGender = ({ values }) => {
  const headings = [
    { title: 'Género' },
    { title: 'Uds.', alignment: 'end' },
    { title: 'Serie-Color', alignment: 'end' },
    { title: 'PVF', alignment: 'end' },
  ];

  return (
    <>
      <div className="flex flex-row justify-around">
        <div className="w-1/3">
          <PieChart
            data={values.byGender.map((e) => ({
              value: e.qty,
              name: e.name,
            }))}
          />
        </div>
        <div className="w-1/2 flex items-center justify-center">
          <div className="w-full">
            <PrettyTable values={values.byGender} headings={headings} sum={true} />
          </div>
        </div>
      </div>
      {/* <div className="p-4 py-8">
        <PrettyTable values={values.byDeposit} headings={headings} sum={true} />
      </div> */}
    </>
  );
};

const TabCategory = ({ values }) => {
  const headings = [
    { defaultSortDirection: 'descending', title: 'Categoría', key: 'name' },
    { defaultSortDirection: 'descending', title: 'Uds.', alignment: 'end', key: 'qty' },
    { defaultSortDirection: 'descending', title: 'Serie-Color', alignment: 'end', key: 'families' },
    { defaultSortDirection: 'descending', title: 'PVF', alignment: 'end', key: 'valuePvf' },
  ];

  return (
    <>
      <div>
        <HorizontalBarsAnimatedChart
          data={{
            name: 'Todo',
            children: values.byCategory.map((e) => ({
              name: e.name,
              children: e.children
                .map((c) => ({
                  value: c.qty,
                  name: c.name,
                }))
                .filter((c) => c.value > 0),
            })),
          }}
        />
      </div>
      <div className="flex flex-row justify-around pt-8">
        <div className="w-4/5 flex items-center justify-center">
          <div className="w-full">
            <PrettyTable values={values.byCategory} headings={headings} sum={true} isSortable={true} />
          </div>
        </div>
      </div>
      {/* <div className="p-4 py-8">
        <PrettyTable values={values.byDeposit} headings={headings} sum={true} />
      </div> */}
    </>
  );
};

/**
 *  TABLAS
 */

const TableRow = (value, index, hasFooterLine) => {
  const { currency } = useCurrency();

  return (
    <IndexTable.Row key={value.id ?? index} selected={false}>
      <IndexTable.Cell>
        <Text alignment="start" variant="headingXs" fontWeight="bold">
          {value.name}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end" variant="headingXs" fontWeight={hasFooterLine ? 'bold' : 'regular'}>
          {Intl.NumberFormat('es-Es').format(value.qty)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end" variant="headingXs" fontWeight={hasFooterLine ? 'bold' : 'regular'}>
          {Intl.NumberFormat('es-Es').format(value.families)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end" variant="headingXs" fontWeight={hasFooterLine ? 'bold' : 'regular'}>
          {Intl.NumberFormat('es-Es', {
            style: 'currency',
            currency: currency.symbol,
          }).format(Math.round(value.valuePvf) * currency.exchange)}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};

const PrettyTable = ({ values, children, headings, sum, isSortable }) => {
  const sortable = isSortable && headings.map(() => isSortable);

  const [currentValues, setCurrentValues] = useState(values ?? []);

  useEffect(() => {
    setCurrentValues(values);
  }, [values]);

  const handleSort = (headingIndex, direction) => {
    const key = headings[headingIndex].key;
    headings[headingIndex].defaultSortDirection = direction === 'ascending' ? 'descending' : 'ascending';
    console.log(headingIndex, direction);
    setCurrentValues(
      [...currentValues].sort((a, b) => (a[key] > b[key] ? 1 : -1) * (direction === 'ascending' ? 1 : -1)),
    );
  };

  return (
    <>
      {children}
      <IndexTable
        headings={headings}
        itemCount={values.length}
        selectable={false}
        lastColumnSticky={false}
        sortable={sortable}
        onSort={handleSort}
        hasZebraStriping={true}
      >
        {currentValues.map((v, i) => TableRow(v, i, false))}
        {sum &&
          TableRow(
            values.reduce(
              (acc, cur) => ({
                ...acc,
                qty: acc.qty + cur.qty,
                families: acc.families + cur.families,
                valuePvf: acc.valuePvf + cur.valuePvf,
              }),
              { name: 'TOTAL', qty: 0, families: 0, valuePvf: 0 },
            ),
            0,
            true,
          )}
      </IndexTable>
    </>
  );
};

export default ViewReportDeposits;
